import { useState, type ReactNode } from "react";
import {
  Building2,
  FlaskConical,
  AlertCircle,
  Cable,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldCheck,
  Tags,
  Users,
  X,
} from "lucide-react";
import type { AuthSessionView, Role } from "@ubisafe/shared";
import { BrandLogo } from "./BrandLogo";

export type AppView = "home" | "clients" | "pre-implementation" | "catalog" | "connectivity" | "users";

interface AppShellProps {
  session: AuthSessionView;
  currentView: AppView;
  onNavigate: (view: AppView) => void;
  onLogout: () => Promise<void>;
  connectivityAttention?: boolean;
  children: ReactNode;
}

const roleLabels: Record<Role, string> = {
  ADMIN: "Administrador",
  PRE_IMPLEMENTATION: "Pré-implantação",
  FINANCE: "Financeiro",
};

export function AppShell({
  session,
  currentView,
  onNavigate,
  onLogout,
  connectivityAttention = false,
  children,
}: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = session.user.roles.includes("ADMIN");
  const canViewClients = isAdmin || session.user.roles.includes("FINANCE");
  const canViewPreImplementation = isAdmin
    || session.user.roles.includes("PRE_IMPLEMENTATION")
    || session.user.roles.includes("FINANCE");
  const initials = session.user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  const navigate = (view: AppView) => {
    onNavigate(view);
    setMobileOpen(false);
  };

  return (
    <div className="app-shell">
      <button
        className={`mobile-overlay ${mobileOpen ? "mobile-overlay--visible" : ""}`}
        aria-label="Fechar navegação"
        onClick={() => setMobileOpen(false)}
      />

      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <BrandLogo />
          <button className="icon-button sidebar__close" aria-label="Fechar menu" onClick={() => setMobileOpen(false)}>
            <X />
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="Navegação principal">
          <button className={currentView === "home" ? "nav-item nav-item--active" : "nav-item"} onClick={() => navigate("home")}>
            <LayoutDashboard aria-hidden="true" />
            <span>Visão geral</span>
          </button>

          <div className="nav-section-label">Tenants</div>
          {canViewClients && (
            <button className={currentView === "clients" ? "nav-item nav-item--active" : "nav-item"} onClick={() => navigate("clients")}>
              <Building2 aria-hidden="true" />
              <span>Clientes</span>
            </button>
          )}
          {canViewPreImplementation && (
            <button
              className={currentView === "pre-implementation" ? "nav-item nav-item--active" : "nav-item"}
              onClick={() => navigate("pre-implementation")}
            >
              <FlaskConical aria-hidden="true" />
              <span>POCs e Trial</span>
            </button>
          )}

          {isAdmin && (
            <>
              <div className="nav-section-label">Administração</div>
              <button
                className={currentView === "connectivity" ? "nav-item nav-item--active" : "nav-item"}
                onClick={() => navigate("connectivity")}
              >
                <Cable aria-hidden="true" />
                <span>Conectividade</span>
                {connectivityAttention && (
                  <small className="nav-item__attention">
                    <AlertCircle aria-hidden="true" /> Atenção
                  </small>
                )}
              </button>
              <button className={currentView === "catalog" ? "nav-item nav-item--active" : "nav-item"} onClick={() => navigate("catalog")}>
                <Tags aria-hidden="true" />
                <span>Classificação</span>
              </button>
              <button className={currentView === "users" ? "nav-item nav-item--active" : "nav-item"} onClick={() => navigate("users")}>
                <Users aria-hidden="true" />
                <span>Usuários</span>
              </button>
            </>
          )}
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar-user">
            <div className="avatar" aria-hidden="true">{initials}</div>
            <div className="sidebar-user__info">
              <strong>{session.user.name}</strong>
              <span>{session.user.email}</span>
              <small>{session.user.roles.map((role) => roleLabels[role]).join(" • ")}</small>
            </div>
          </div>
          <button className="nav-item nav-item--logout" onClick={() => void onLogout()}>
            <LogOut aria-hidden="true" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      <div className="app-content">
        <header className="topbar">
          <button className="icon-button topbar__menu" aria-label="Abrir menu" onClick={() => setMobileOpen(true)}>
            <Menu />
          </button>
          <div className="topbar__title">
            <ShieldCheck aria-hidden="true" />
            <span>Ambiente interno UbiSafe</span>
          </div>
          <div className="topbar__session">Sessão válida até {new Date(session.expiresAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
        </header>
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
}
